import type { ChangeEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  DisplayProps,
  FlexBasisProps,
  FlexProps,
  ResponsiveValue,
  SpaceProps,
} from 'styled-system';
import { display, flex, flexBasis, space } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { ArticleMaterialType } from 'common/types/Article';
import { isGiftCard } from 'common/utils/gift-cards';
import type { AddToCartProps } from '../components/types';
import CtaButton from './CtaButton';
import Quantity from './Quantity';

interface FormControlsProps extends Pick<
  AddToCartProps,
  | 'buttonKind'
  | 'selectWidth'
  | 'withButtonAnimation'
  | 'hideQuantity'
  | 'ctaLabel'
  | 'isRounded'
  | 'withIcon'
  | 'materialType'
  | 'disableOnSubmit'
> {
  value: string;
  options: number[];
  onSelect: (params: ChangeEvent<HTMLSelectElement>) => void;
  isLoading?: boolean;
  addedToCart?: boolean;
  className?: string;
  isSticky?: boolean;
  minWidth?: ResponsiveValue<string | number>;
}

const FormControlsContainer = styled.div<DisplayProps & FlexProps & FlexBasisProps & SpaceProps>`
  ${display};
  ${flex};
  ${flexBasis};
  ${space};
`;

const formControlContainerStyles = {
  display: 'flex',
  flex: 1,
  flexBasis: ['100%', 'inherit', '100%'],
};

const shouldDisableCTA = ({
  disableOnSubmit,
  addedToCart,
  isLoading,
}: Pick<FormControlsProps, 'disableOnSubmit' | 'addedToCart' | 'isLoading'>) => {
  if (disableOnSubmit) {
    return addedToCart;
  }

  return isLoading;
};

const getCtaLabel = (materialType: ArticleMaterialType, hideQuantity: boolean) => {
  if (isGiftCard(materialType)) {
    return 'h24_to_checkout';
  }

  return hideQuantity ? 'h24_add_to_cart_short' : 'add_to_cart';
};

const FormControls = ({
  isLoading,
  buttonKind = 'primary',
  withButtonAnimation,
  disableOnSubmit,
  addedToCart,
  hideQuantity,
  ctaLabel: ctaLabelProp,
  isRounded,
  withIcon,
  materialType,
  className,
  isSticky,
  minWidth,
  ...quantityProps
}: FormControlsProps) => {
  const { t } = useLocalization();
  const isCtaDisabled = shouldDisableCTA({
    disableOnSubmit,
    addedToCart,
    isLoading,
  });
  const ctaLabel = ctaLabelProp ?? getCtaLabel(materialType, hideQuantity);

  return (
    <FormControlsContainer
      {...formControlContainerStyles}
      {...(isSticky
        ? {
            px: ['sp_20', 'sp_24', 'sp_16', 'sp_24'],
          }
        : {})}
    >
      <Quantity {...quantityProps} isHidden={hideQuantity} isDisabled={isCtaDisabled} />
      <CtaButton
        isLoading={isLoading}
        isDisabled={isCtaDisabled}
        isSuccess={withButtonAnimation ? addedToCart : false}
        label={t(ctaLabel)}
        successLabel={t('h24_added_to_cart')}
        buttonKind={buttonKind}
        isRounded={isRounded}
        withIcon={withIcon}
        className={className}
        minWidth={minWidth}
      />
    </FormControlsContainer>
  );
};

export default FormControls;
